/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { AiLog, AI_OUTCOMES } from '@src/app/model/ai-log';
import { AiLogService } from '@src/app/modules/crm/ai-log.service';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { AiLogPageNavRoute } from '@src/app/routes/ai-log-page-nav';
import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormPickLinkComponent } from '@src/app/shared/form/form-pick-link/form-pick-link.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { recentPeriods } from '@src/app/modules/crm/request-analysis/analysis-period';

/**
 * Every question the application has put to an AI, and what a person did with the answer. Read only
 * on purpose - it is a record, and one nobody can edit is the only kind worth keeping - so the list
 * has no new button and the detail no save.
 */
@Component({
    selector: 'app-ai-log-page',
    templateUrl: './ai-log-page.component.html',
    imports: [FormPageComponent]
})
export class AiLogPageComponent extends AbstractPageComponent {
    private dataSvc = inject(AiLogService);

    /** No pick options - the request a call was made about is a fact of the row, not a choice. */
    requestLink = FormPickLinkComponent.make({
        label: $localize`Request`,
        value: 'relatedId',
        textValue: (o: AiLog) => o.question?.split('\n')[0] ?? '',
        nav: ServiceRequestPageNavRoute,
    }).setReadonly();

    /** Its items are the teams the loaded rows name - see collectOmcs. */
    omcField = FormPicklistComponent.make($localize`OMC`, 'teamId', null, { items: [] });

    config = new FormConfig({
        navRoute: AiLogPageNavRoute,
        title: $localize`AI Responses`,
        help: $localize`Every answer our AI assistant has given, what it cost, and what your team did with it`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FormDateTimeComponent.make($localize`Asked`, 'createdAt', { readonly: true }),
                FormPicklistComponent.make($localize`Month`, 'month', null,
                    { items: recentPeriods('month', 24) }, { visible: Field.noForm }),
                this.omcField,
                this.requestLink,
                FormTextComponent.make($localize`Asked About`, 'type'),
                FormPicklistComponent.make($localize`Outcome`, 'outcome', null, { items: AI_OUTCOMES }),
                FormTextComponent.make($localize`Comment Kept`, 'commentMatch', {
                    formLine: 'reviewChanges',
                    calculateValue: (o: AiLog) => o.commentMatch === null || o.commentMatch === undefined
                        ? '' : `${Math.round(o.commentMatch)}%`,
                    hint: $localize`How much of the assistant's wording survived - 100% means it was sent untouched`,
                }),
                FormTextComponent.make($localize`Fields Changed`, 'fieldsChanged', { formLine: 'reviewChanges' }),
                FormNumberComponent.make($localize`Cost`, 'costUsd', 
                    {format:'currency', width: 10, formatParms: '1.4-4'},{
                    calculateValue: (o: AiLog) => o.costUsd * 3
                }),
                FormTextComponent.make($localize`Created By`, 'createdByName',
                    { formLine: 'createdReviewedBy', visible: Field.formOnly }),
                FormTextComponent.make($localize`Reviewed By`, 'reviewedByName',
                    { formLine: 'createdReviewedBy', visible: Field.formOnly }),

                FormTextComponent.make($localize`Model`, 'aiModel', { formColumn: 2, visible: Field.formOnly }),
                FormTextComponent.make($localize`Tokens In`, 'inTokens',
                    { formColumn: 2, formLine: 'tokens', visible: Field.formOnly }),
                FormTextComponent.make($localize`Tokens Out`, 'outTokens',
                    { formColumn: 2, formLine: 'tokens', visible: Field.formOnly }),
                FormTextComponent.make($localize`Cache Read`, 'cacheReadTokens',
                    { formColumn: 2, formLine: 'cacheTokens', visible: Field.formOnly }),
                FormTextComponent.make($localize`Cache Write`, 'cacheWriteTokens',
                    { formColumn: 2, formLine: 'cacheTokens', visible: Field.formOnly }),
                FormTextComponent.make($localize`Took (ms)`, 'durationMs', { formColumn: 2, visible: Field.formOnly }),
                FormTextComponent.make($localize`Prompt`, 'promptHash', {
                    formColumn: 2, visible: Field.formOnly,
                    calculateValue: (o: AiLog) => o.promptHash?.substring(0, 12) ?? '',
                    hint: $localize`Identifies the prompt used - a different value means the prompt was edited`,
                }),
                FormTextComponent.make($localize`Context`, 'contextHash', {
                    formColumn: 2, visible: Field.formOnly,
                    calculateValue: (o: AiLog) => o.contextHash?.substring(0, 12) ?? '',
                    hint: $localize`Identifies everything the assistant was shown, notices and documents included`,
                }),
                FormTextComponent.make($localize`Failed`, 'errorMessage', { formColumn: 2, visible: Field.formOnly }),
                FormTextComponent.make($localize`Requester Email`, 'requesterEmail',
                    { formColumn: 2, visible: Field.formOnly }),

                FormTextAreaComponent.make($localize`What was asked`, 'question',
                    { formColumn: 3, visible: Field.formOnly }),
                FormTextAreaComponent.make($localize`What it answered`, 'proposedComment', {
                    formColumn: 3, visible: Field.formOnly,
                    calculateValue: (o: AiLog) => AiLogPageComponent.describe(o.proposed),
                }),
                FormTextAreaComponent.make($localize`What was sent`, 'finalComment', {
                    formColumn: 3, visible: Field.formOnly,
                    calculateValue: (o: AiLog) => AiLogPageComponent.describe(o.final),
                }),
                FormTextAreaComponent.make($localize`How it was reviewed`, 'reviewSummary', {
                    formColumn: 3, visible: Field.formOnly,
                    calculateValue: (o: AiLog) => AiLog.reviewSummary(o),
                }),
            ],
            formLayout: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '50%' }] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        allowNew: false,
        allowEdit: false,
        allowDelete: false,
        beforeList: this.collectOmcs.bind(this),
    });

    /**
     * Each row arrives with its team, so the OMC filter and chart are listed from the rows rather
     * than from a separate call. Teams seen in earlier loads are kept, so filtering to one OMC still
     * leaves the others to pick from.
     */
    private collectOmcs(logs: AiLog[]): AiLog[] {
        const omcs = new Map(this.omcField.picklist.items.map(omc => [omc.id, omc]));
        for (const log of logs) {
            if (log.team && !omcs.has(log.teamId)) {
                omcs.set(log.teamId, { id: log.teamId, name: log.team.name });
            }
        }
        this.omcField.setPicklistItems([...omcs.values()]);
        return logs;
    }

    /** The answer as prose - the JSON is the record, this is the bit a person reads. */
    private static describe(answer: AiLog['proposed']): string {
        if (!answer) {
            return '';
        }
        const heading = [answer.srTopic, answer.srClass, answer.srStatus].filter(v => v).join(' / ');
        const comment = String(answer.comment ?? answer['answer'] ?? '').replace(/<br\s*\/?>/g, '\n').replace(/<[^>]+>/g, '');
        return heading ? `${heading}\n\n${comment}` : comment;
    }
}
